import { ref } from 'vue'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

/**
 * 🔒 Module-level state (shared singleton)
 */
const deferredEvent = ref<BeforeInstallPromptEvent | null>(null)
const canInstall = ref(false)
const isInstalled = ref(false)

let initialized = false
const DISMISSED_KEY = 'pwa-install-dismissed'

/**
* Internal helpers
*/
const checkInstalled = () => {
  const standalone = window.matchMedia('(display-mode: standalone)').matches
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true

  isInstalled.value = standalone || iosStandalone
}

const handleBeforeInstallPrompt = (e: Event) => {
  if (localStorage.getItem(DISMISSED_KEY)) return

  e.preventDefault()
  deferredEvent.value = e as BeforeInstallPromptEvent
  canInstall.value = true
}

const handleAppInstalled = () => {
  isInstalled.value = true
  canInstall.value = false
  deferredEvent.value = null
}

/**
 * 🔌 Initialize only once
 */
const init = () => {
  if (initialized) return
  initialized = true

  checkInstalled()

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.addEventListener('appinstalled', handleAppInstalled)
}

/**
 * 🚀 Public API
 */
const install = async () => {
  if (!deferredEvent.value) return

  await deferredEvent.value.prompt()

  // const { outcome } = await deferredEvent.value.userChoice
  // console.log(`User response to the installation prompt: ${outcome}`)

  deferredEvent.value = null
  canInstall.value = false
}

const dismiss = () => {
  canInstall.value = false
  localStorage.setItem(DISMISSED_KEY, 'true')
}

export function usePWA() {
  // ensure initialized once
  if (typeof window !== 'undefined') {
    init()
  }

  return {
    canInstall,
    isInstalled,
    install,
    dismiss,
  }
}
